import CommandCenterHeader from "@components/CommandCenterHeader/CommandCenterHeader";
import {
  Autocomplete,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  PlanGenerationAttemptOut,
  useAutomationIntegrationApiV1GetAutomationMetricsQuery,
} from "@providers/hop-ord-server/api";
import dayjs, { Dayjs } from "dayjs";
import { useMemo, useState } from "react";
import MetricsTable from "./components/MetricsTable";
import MetricsSummary from "./components/MetricsSummary";
import { Role } from "@enums";
import { formatTenantNameForDisplay } from "@utils";
import { useCheckUserRole } from "@utils/useCheckUserRole";
import { useNavigate } from "react-router-dom";

const Metrics = () => {
  const isViewMetricsUser = useCheckUserRole(Role.METRICS);
  const navigate = useNavigate();
  if (!isViewMetricsUser) {
    navigate("/error");
  }

  const [startDate, setStartDate] = useState<Dayjs>(() =>
    dayjs().subtract(30, "day"),
  );
  const [endDate, setEndDate] = useState<Dayjs>(() => dayjs());

  const { data, isLoading, isError } =
    useAutomationIntegrationApiV1GetAutomationMetricsQuery(
      {
        startDate: startDate.startOf("day").toISOString(),
        endDate: endDate.endOf("day").toISOString(),
      },
      {
        skip: !startDate || !endDate || startDate.isAfter(endDate),
      },
    );

  const rows: PlanGenerationAttemptOut[] = useMemo(
    () => (Array.isArray(data) ? data : []),
    [data],
  );

  const tenantOptions = useMemo(() => {
    const names = new Set<string>();
    for (const r of rows) {
      if (r.tenantName) names.add(r.tenantName);
    }
    return [...names].sort((a, b) =>
      formatTenantNameForDisplay(a).localeCompare(
        formatTenantNameForDisplay(b),
      ),
    );
  }, [rows]);

  const [selectedTenants, setSelectedTenants] = useState<string[]>([]);
  const [showInProgressPlans, setShowInProgressPlans] = useState(true);
  const [showSuccessPlans, setShowSuccessPlans] = useState(true);
  const [showErrorPlans, setShowErrorPlans] = useState(true);

  const filteredRows = useMemo(() => {
    let next = rows;
    if (selectedTenants.length > 0) {
      const selected = new Set(selectedTenants);
      next = next.filter((r) => selected.has(r.tenantName));
    }
    return next.filter((r) => {
      if (!r.finishedAt) return showInProgressPlans;
      if (r.success) return showSuccessPlans;
      return showErrorPlans;
    });
  }, [
    rows,
    selectedTenants,
    showInProgressPlans,
    showSuccessPlans,
    showErrorPlans,
  ]);

  const showMetrics = !isLoading && !isError && filteredRows.length > 0;

  return (
    <Stack
      sx={{
        gap: 3,
        padding: 2,
        height: 1,
        maxHeight: 1,
        width: 1,
        maxWidth: "1940px",
        minWidth: "1940px",
        marginLeft: "auto",
        marginRight: "auto",
      }}
    >
      <CommandCenterHeader title="Plan Generation Metrics" />
      {isLoading && <CircularProgress />}
      <Stack
        direction="row"
        spacing={2}
        sx={{
          alignItems: "flex-start",
          justifyContent: "space-between",
          width: 1,
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          sx={{
            alignItems: "center",
          }}
        >
          <Stack spacing={2}>
            <Button
              variant="outlined"
              onClick={() => {
                setStartDate(dayjs().subtract(30, "day"));
                setEndDate(dayjs());
              }}
            >
              Reset to Last 30 Days
            </Button>
            <DatePicker
              label="Start date"
              value={startDate}
              onChange={(v) => v && setStartDate(v)}
              format="YYYY-MM-DD"
            />
            <DatePicker
              label="End date"
              value={endDate}
              onChange={(v) => v && setEndDate(v)}
              format="YYYY-MM-DD"
            />
          </Stack>
          <MetricsSummary rows={filteredRows} />
        </Stack>
        <Stack
          spacing={1.5}
          sx={{
            alignItems: "flex-end",
            alignSelf: "flex-start",
          }}
        >
          <Stack
            direction="row"
            spacing={0.5}
            sx={{
              alignItems: "center",
              flexWrap: "wrap",
              justifyContent: "flex-end",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={showInProgressPlans}
                  onChange={(_, checked) => setShowInProgressPlans(checked)}
                />
              }
              label="In progress"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={showSuccessPlans}
                  onChange={(_, checked) => setShowSuccessPlans(checked)}
                />
              }
              label="Success"
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={showErrorPlans}
                  onChange={(_, checked) => setShowErrorPlans(checked)}
                />
              }
              label="Error"
            />
          </Stack>
          <Autocomplete
            multiple
            size="small"
            limitTags={2}
            options={tenantOptions}
            value={selectedTenants}
            onChange={(_, newValue) => setSelectedTenants(newValue)}
            getOptionLabel={(option) => formatTenantNameForDisplay(option)}
            disableCloseOnSelect
            disablePortal
            sx={{ minWidth: 280, maxWidth: 400, width: "100%" }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tenants"
                placeholder="All tenants"
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option}>
                <Checkbox checked={selectedTenants.includes(option)} />
                {formatTenantNameForDisplay(option)}
              </li>
            )}
          />
        </Stack>
      </Stack>
      {!rows.length && (
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
          }}
        >
          No data found
        </Typography>
      )}
      {rows.length > 0 && !filteredRows.length && (
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
          }}
        >
          No rows match the selected filters.
        </Typography>
      )}
      {showMetrics && <MetricsTable rows={filteredRows} />}
    </Stack>
  );
};

export default Metrics;
